"use client";

import React from "react";
import { Button, EmptyState } from "@west-tennessee-consulting/ui";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { useGoogleDriveReauth } from "@/lib/hooks/google-drive/use-google-drive-reauth";

interface GoogleDriveAccessRequiredStateProps {
  isSecondAccount?: boolean;
}

/**
 * Empty state shown when the Drive browser has no access (missing Drive scope /
 * 401 / 403). Unlike a bare message, this offers the immediate fix — a one-click
 * "Reconnect Google Drive" button that starts the OAuth re-auth flow right here.
 * See the ERROR_STATES_ACTIONABLE rule.
 */
export function GoogleDriveAccessRequiredState({
  isSecondAccount = false,
}: GoogleDriveAccessRequiredStateProps) {
  const { isReauthing, startReauth } = useGoogleDriveReauth({ isSecondAccount });

  return (
    <EmptyState
      icon={ShieldAlert}
      title="Google Drive access required"
      description="We don't have permission to read this account's Drive. Reconnect to grant Drive access and pick up where you left off."
      action={
        <Button onClick={startReauth} disabled={isReauthing} variant="default">
          {isReauthing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Redirecting…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reconnect Google Drive
            </>
          )}
        </Button>
      }
    />
  );
}
